import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Loader2, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageLayout from '@/components/layout/PageLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        throw new Error(typeof detail === 'string' ? detail : 'Something went wrong');
      }
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <div className="min-h-[75vh] flex items-center justify-center py-12 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {sent ? (
            <div className="text-center" data-testid="forgot-password-success">
              <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-taxera-charcoal mb-3">{t('auth.checkEmail')}</h1>
              <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                {t('auth.resetEmailSent')}
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-taxera-blue font-semibold hover:underline"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('auth.backToSignIn')}
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="font-heading text-3xl font-bold text-taxera-charcoal mb-2" data-testid="forgot-password-title">
                  {t('auth.forgotTitle')}
                </h1>
                <p className="text-sm text-slate-500">
                  {t('auth.forgotSubtitle')}
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 text-red-600 rounded-xl text-sm mb-4" data-testid="forgot-password-error">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.emailLabel')}
                    required
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:border-taxera-blue focus:ring-1 focus:ring-taxera-blue outline-none"
                    data-testid="forgot-password-email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 taxera-gradient text-white rounded-xl font-semibold text-sm hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="forgot-password-submit"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t('auth.sendResetLink')}
                </button>
              </form>

              <p className="text-center text-sm text-slate-500 mt-6">
                <Link to="/login" className="text-taxera-blue font-semibold hover:underline" data-testid="back-to-login-from-forgot">
                  {t('auth.backToSignIn')}
                </Link>
              </p>
            </>
          )}
        </motion.div>
      </div>
    </PageLayout>
  );
};

export default ForgotPasswordPage;
